import { useEffect, useMemo, useRef, useState } from "react";

import Card from "@mui/material/Card";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import useMediaQuery from "@mui/material/useMediaQuery";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { useMaterialUIController } from "context";
import { deleteBudget, getBudget, hasPersistedBudget, upsertBudget } from "layouts/orcamento/budgetApi";
import {
  getRevenueExpenseCategorySummary,
  getRevenueExpensesSummary,
} from "layouts/receitaXdespesa/components/receitasxdespesas/revenueExpensesApi";
import { formatRevenueExpenseCategory } from "layouts/receitaXdespesa/components/receitasxdespesas/revenueExpenseCategories";
import ValueVisibilityToggleButton from "components/ValueVisibilityToggleButton";
import {
  HIDDEN_CURRENCY_LABEL,
  HIDDEN_PERCENTAGE_LABEL,
  getStoredValuesHidden,
  persistValuesHidden,
} from "utils/valueVisibility";

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const numberFormatter = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const percentFormatter = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const expenseChartColors = [
  "#1a73e8",
  "#4caf50",
  "#fb8c00",
  "#ef5350",
  "#8e24aa",
  "#00acc1",
  "#6d4c41",
  "#fdd835",
];

const getMonthKey = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const getDateFromMonthKey = (monthKey) => {
  const [year, month] = monthKey.split("-").map(Number);
  return new Date(year, month - 1, 1);
};

const addMonths = (monthKey, amount) => {
  const date = getDateFromMonthKey(monthKey);
  date.setMonth(date.getMonth() + amount);
  return getMonthKey(date);
};

const createEmptyBudget = () => ({
  plannedRevenue: "",
  plannedExpenses: "",
});

const getReferenceDate = (monthKey) => `${monthKey}-15T12:00:00`;

const toNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatCurrency = (value) => currencyFormatter.format(toNumber(value));

const formatPercent = (value) => `${percentFormatter.format(toNumber(value))}%`;

const normalizeExpenseCategorySummary = (item, index) => ({
  category: item.category || "Sem categoria",
  label: formatRevenueExpenseCategory(item.category || "Sem categoria"),
  value: Math.max(Number(item.value ?? 0), 0),
  configuredLimitValue: item.configuredLimitValue != null ? Number(item.configuredLimitValue) : null,
  hasConfiguredLimit: Boolean(item.hasConfiguredLimit),
  color: expenseChartColors[index % expenseChartColors.length],
});

const getMonthProgress = (monthKey) => {
  const now = new Date();
  const selectedDate = getDateFromMonthKey(monthKey);
  const selectedYear = selectedDate.getFullYear();
  const selectedMonth = selectedDate.getMonth();
  const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
  const isCurrentMonth = selectedYear === now.getFullYear() && selectedMonth === now.getMonth();
  const isPastMonth = selectedDate < new Date(now.getFullYear(), now.getMonth(), 1);
  const elapsedDays = isPastMonth ? daysInMonth : isCurrentMonth ? now.getDate() : 0;
  const progress = daysInMonth > 0 ? Math.min((elapsedDays / daysInMonth) * 100, 100) : 0;

  return {
    daysInMonth,
    elapsedDays,
    progress,
  };
};

const getProjection = ({ actualValue, plannedValue }) => {
  const numericActualValue = toNumber(actualValue);
  const numericPlannedValue = toNumber(plannedValue);

  return Math.max(numericActualValue, numericPlannedValue);
};

const calendarProgressPalette = {
  fill: "#1a73e8",
  soft: "rgba(26, 115, 232, 0.12)",
  border: "rgba(26, 115, 232, 0.22)",
};

const hiddenMetricPalette = {
  fill: "rgba(148, 163, 184, 0.85)",
  soft: "rgba(148, 163, 184, 0.14)",
  border: "rgba(148, 163, 184, 0.24)",
};

const getExpenseProgressPalette = (progress) => {
  if (progress >= 100) {
    return {
      fill: "#ef5350",
      soft: "rgba(239, 83, 80, 0.14)",
      border: "rgba(239, 83, 80, 0.24)",
    };
  }

  if (progress >= 75) {
    return {
      fill: "#fb8c00",
      soft: "rgba(251, 140, 0, 0.14)",
      border: "rgba(251, 140, 0, 0.24)",
    };
  }

  return {
    fill: "#4caf50",
    soft: "rgba(76, 175, 80, 0.14)",
    border: "rgba(76, 175, 80, 0.24)",
  };
};

const getCategoryShare = (value, total) => (total > 0 ? (toNumber(value) / total) * 100 : 0);

const buildDonutGradient = (items, total, darkMode) => {
  if (!items.length || total <= 0) {
    return darkMode
      ? "conic-gradient(rgba(148, 163, 184, 0.26) 0 100%)"
      : "conic-gradient(rgba(226, 232, 240, 0.92) 0 100%)";
  }

  let accumulated = 0;

  return `conic-gradient(${items
    .map((item, index) => {
      const start = accumulated;
      const share = index === items.length - 1 ? 100 - accumulated : getCategoryShare(item.value, total);
      accumulated += share;

      return `${item.color} ${start}% ${accumulated}%`;
    })
    .join(", ")})`;
};

const formatOverLimitCategoriesDetail = (categoriesOverLimit, displayCurrency) => {
  const sortedCategories = categoriesOverLimit
    .slice()
    .sort(
      (a, b) =>
        b.value - toNumber(b.configuredLimitValue) - (a.value - toNumber(a.configuredLimitValue))
    );
  const visibleCategories = sortedCategories.slice(0, 3).map((item) => {
    const exceededValue = item.value - toNumber(item.configuredLimitValue);

    return `${item.label} passou ${displayCurrency(exceededValue)}.`;
  });
  const hiddenCount = sortedCategories.length - visibleCategories.length;
  const hiddenText =
    hiddenCount > 0
      ? `\nE mais ${hiddenCount} categoria${hiddenCount > 1 ? "s" : ""}.`
      : "";

  return `${visibleCategories.join("\n")}${hiddenText}`;
};

const buildBudgetInsights = ({
  categoryItems,
  totalExpenses,
  plannedExpenses,
  freeToSpend,
  projectedExpenseDelta,
  displayCurrency,
  displayPercent,
}) => {
  const insights = [];
  const topCategory = categoryItems[0];
  const categoriesWithLimit = categoryItems.filter(
    (item) => item.hasConfiguredLimit && toNumber(item.configuredLimitValue) > 0
  );
  const categoriesOverLimit = categoriesWithLimit.filter(
    (item) => item.value > toNumber(item.configuredLimitValue)
  );
  const topThreeTotal = categoryItems.slice(0, 3).reduce((total, item) => total + item.value, 0);

  if (topCategory) {
    insights.push({
      icon: "north_east",
      title: `Maior saída: ${topCategory.label}`,
      detail: `${displayCurrency(topCategory.value)} (${displayPercent(getCategoryShare(topCategory.value, totalExpenses))} do total)`,
      color: topCategory.color,
    });
  } else {
    insights.push({
      icon: "check_circle",
      title: "Nenhuma despesa lançada",
      detail: "O mês ainda não tem despesas registradas.",
      color: "#4caf50",
    });
  }

  if (plannedExpenses <= 0) {
    insights.push({
      icon: "info",
      title: "Sem orçamento de despesas",
      detail: "Defina um valor planejado para comparar o mês.",
      color: "#1a73e8",
    });
  } else if (projectedExpenseDelta > 0) {
    insights.push({
      icon: "warning",
      title: "Despesas acima do planejado",
      detail: `O desvio atual/projetado é de ${displayCurrency(projectedExpenseDelta)}.`,
      color: "#ef5350",
    });
  } else {
    insights.push({
      icon: "check_circle",
      title: "Dentro do orçamento",
      detail: `${displayCurrency(freeToSpend)} ainda livre para o mês.`,
      color: "#4caf50",
    });
  }

  if (categoriesOverLimit.length > 0) {
    insights.push({
      icon: "priority_high",
      title: `${categoriesOverLimit.length} categoria${categoriesOverLimit.length > 1 ? "s" : ""} acima do limite`,
      detail: formatOverLimitCategoriesDetail(categoriesOverLimit, displayCurrency),
      color: "#fb8c00",
    });
  } else if (categoriesWithLimit.length > 0) {
    insights.push({
      icon: "verified",
      title: "Limites por categoria OK",
      detail: "Nenhuma categoria com limite configurado estourou.",
      color: "#4caf50",
    });
  } else {
    insights.push({
      icon: "tune",
      title: "Sem limites por categoria",
      detail: "Configure limites para enxergar desvios por área.",
      color: "#1a73e8",
    });
  }

  if (totalExpenses > 0) {
    insights.push({
      icon: "donut_large",
      title: "Concentração das despesas",
      detail: `As 3 maiores categorias somam ${displayPercent(getCategoryShare(topThreeTotal, totalExpenses))}.`,
      color: "#8e24aa",
    });
  }

  return insights;
};

function ProjectionProgressBar({ label, helperLabel, badgeLabel, progress, palette, darkMode }) {
  const displayProgress = Math.min(Math.max(toNumber(progress), 0), 100);
  const badgePosition = `clamp(52px, ${displayProgress}%, calc(100% - 52px))`;

  return (
    <MDBox sx={{ position: "relative", minHeight: 82 }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={0.75}>
        <MDTypography variant="caption" color="text">
          {label}
        </MDTypography>
        <MDTypography variant="caption" color="text" sx={{ whiteSpace: "nowrap" }}>
          {helperLabel}
        </MDTypography>
      </MDBox>

      <MDBox
        sx={{
          position: "absolute",
          top: 30,
          left: badgePosition,
          transform: "translateX(-50%)",
          height: 27,
          px: 1.5,
          py: 0.65,
          borderRadius: 999,
          backgroundColor: palette.soft,
          border: `1px solid ${palette.border}`,
          boxShadow: darkMode ? "0 10px 24px rgba(15, 23, 42, 0.28)" : "0 10px 24px rgba(15, 23, 42, 0.08)",
          zIndex: 2,
        }}
      >
        <MDTypography
          variant="caption"
          fontWeight="bold"
          sx={{ color: palette.fill, whiteSpace: "nowrap", top: -11, position: "relative" }}
        >
          {badgeLabel}
        </MDTypography>
      </MDBox>

      <MDBox
        sx={{
          position: "absolute",
          top: 56,
          left: badgePosition,
          transform: "translateX(-50%)",
          width: 2,
          height: 9,
          borderRadius: 999,
          backgroundColor: palette.border,
          zIndex: 1,
        }}
      />

      <MDBox
        sx={{
          position: "absolute",
          inset: "auto 0 8px 0",
          height: 10,
          borderRadius: 999,
          backgroundColor: darkMode ? "rgba(148, 163, 184, 0.22)" : "#e9ecef",
        }}
      />
      <MDBox
        sx={{
          position: "absolute",
          inset: "auto auto 8px 0",
          width: `${displayProgress}%`,
          height: 10,
          borderRadius: 999,
          backgroundColor: palette.fill,
          minWidth: displayProgress > 0 ? 10 : 0,
        }}
      />
    </MDBox>
  );
}

function SummaryCard({ icon, title, value, detail, color }) {
  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={2} display="flex" gap={1.5} alignItems="center">
        <MDBox
          sx={{
            width: 42,
            height: 42,
            borderRadius: "10px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            backgroundColor: `rgba(${color}, 0.14)`,
            color: `rgb(${color})`,
          }}
        >
          <Icon>{icon}</Icon>
        </MDBox>
        <MDBox minWidth={0}>
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {title}
          </MDTypography>
          <MDTypography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
            {value}
          </MDTypography>
          {detail && (
            <MDTypography variant="caption" color="text">
              {detail}
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
    </Card>
  );
}

function ExpenseDistributionChart({ items, total, darkMode, displayCurrency, displayPercent, valuesHidden }) {
  const displayedItems = items.slice(0, 6);

  return (
    <MDBox>
      <MDBox
        sx={{
          width: { xs: 190, sm: 220 },
          height: { xs: 190, sm: 220 },
          mx: "auto",
          borderRadius: "50%",
          background: valuesHidden ? buildDonutGradient([], 0, darkMode) : buildDonutGradient(items, total, darkMode),
          boxShadow: darkMode ? "inset 0 0 0 1px rgba(148, 163, 184, 0.12)" : "inset 0 0 0 1px rgba(52, 71, 103, 0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <MDBox
          sx={{
            width: "58%",
            height: "58%",
            borderRadius: "50%",
            backgroundColor: darkMode ? "rgba(30, 41, 59, 0.98)" : "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            boxShadow: darkMode ? "0 16px 32px rgba(2, 6, 23, 0.18)" : "0 16px 32px rgba(15, 23, 42, 0.08)",
            px: 1.5,
          }}
        >
          <MDTypography variant="caption" color="text">
            Total gasto
          </MDTypography>
          <MDTypography variant="h6" fontWeight="bold" textAlign="center">
            {displayCurrency(total)}
          </MDTypography>
        </MDBox>
      </MDBox>

      <MDBox mt={2} display="grid" gap={1}>
        {displayedItems.length > 0 ? (
          displayedItems.map((item) => (
            <MDBox
              key={item.category}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1.5}
              sx={{ minWidth: 0 }}
            >
              <MDBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                <MDBox
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: item.color,
                    flexShrink: 0,
                  }}
                />
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {item.label}
                </MDTypography>
              </MDBox>
              <MDTypography variant="caption" fontWeight="medium" sx={{ whiteSpace: "nowrap" }}>
                {displayPercent(getCategoryShare(item.value, total))}
              </MDTypography>
            </MDBox>
          ))
        ) : (
          <MDTypography variant="button" color="text">
            Sem despesas por categoria neste mês.
          </MDTypography>
        )}
      </MDBox>
    </MDBox>
  );
}

function BudgetInsightList({ insights, darkMode }) {
  return (
    <MDBox display="grid" gap={1.5}>
      {insights.map((insight) => {
        const detailLines = `${insight.detail}`.split("\n").filter(Boolean);

        return (
          <MDBox
            key={`${insight.title}-${insight.detail}`}
            display="flex"
            gap={1.25}
            sx={{
              p: 1.5,
              borderRadius: "10px",
              backgroundColor: darkMode ? "rgba(15, 23, 42, 0.16)" : "rgba(248, 249, 250, 0.86)",
              border: `1px solid ${darkMode ? "rgba(148, 163, 184, 0.12)" : "rgba(52, 71, 103, 0.08)"}`,
            }}
          >
            <MDBox
              sx={{
                width: 32,
                height: 32,
                borderRadius: "10px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                backgroundColor: `${insight.color}22`,
                color: insight.color,
              }}
            >
              <Icon fontSize="small">{insight.icon}</Icon>
            </MDBox>
            <MDBox minWidth={0}>
              <MDTypography variant="button" fontWeight="bold">
                {insight.title}
              </MDTypography>
              <MDBox display="grid" gap={1} mt={0.25}>
                {detailLines.map((detailLine, index) => (
                  <MDTypography key={`${detailLine}-${index}`} variant="caption" color="text" display="block">
                    {detailLine}
                  </MDTypography>
                ))}
              </MDBox>
            </MDBox>
          </MDBox>
        );
      })}
    </MDBox>
  );
}

function Orcamento() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const isMobileBudgetSummary = useMediaQuery("(max-width:900px)", { noSsr: true });
  const [areBudgetValuesHidden, setAreBudgetValuesHidden] = useState(getStoredValuesHidden);
  const [selectedMonth, setSelectedMonth] = useState(getMonthKey());
  const [budget, setBudget] = useState(createEmptyBudget);
  const [summary, setSummary] = useState({ revenue: 0, expenses: 0, remaningValue: 0 });
  const [expenseCategorySummary, setExpenseCategorySummary] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpenseSummaryLoading, setIsExpenseSummaryLoading] = useState(true);
  const [isBudgetReady, setIsBudgetReady] = useState(false);
  const [openSnackMessage, setOpenSnackMessage] = useState(null);
  const [isBudgetSummaryExpanded, setIsBudgetSummaryExpanded] = useState(false);
  const skipNextPlanSaveRef = useRef(false);

  useEffect(() => {
    persistValuesHidden(areBudgetValuesHidden);
  }, [areBudgetValuesHidden]);

  useEffect(() => {
    let isMounted = true;

    const loadBudget = async () => {
      setIsBudgetReady(false);

      try {
        const data = await getBudget(getReferenceDate(selectedMonth));

        if (!isMounted) {
          return;
        }

        skipNextPlanSaveRef.current = true;
        setBudget((currentBudget) => ({
          ...currentBudget,
          plannedRevenue: Number(data?.plannedRevenue ?? 0).toFixed(2),
          plannedExpenses: Number(data?.plannedExpenses ?? 0).toFixed(2),
        }));
      } catch (error) {
        if (!isMounted) {
          return;
        }

        skipNextPlanSaveRef.current = true;
        setBudget((currentBudget) => ({
          ...currentBudget,
          plannedRevenue: "",
          plannedExpenses: "",
        }));
        setOpenSnackMessage({ type: "error", message: `${error}` });
      } finally {
        if (isMounted) {
          setIsBudgetReady(true);
        }
      }
    };

    loadBudget();

    return () => {
      isMounted = false;
    };
  }, [selectedMonth]);

  useEffect(() => {
    if (!isBudgetReady) {
      return undefined;
    }

    if (skipNextPlanSaveRef.current) {
      skipNextPlanSaveRef.current = false;
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        await upsertBudget({
          referenceMonth: getReferenceDate(selectedMonth),
          plannedRevenue: toNumber(budget.plannedRevenue),
          plannedExpenses: toNumber(budget.plannedExpenses),
        });
      } catch (error) {
        setOpenSnackMessage({ type: "error", message: `${error}` });
      }
    }, 650);

    return () => window.clearTimeout(timeoutId);
  }, [budget.plannedRevenue, budget.plannedExpenses, isBudgetReady, selectedMonth]);

  useEffect(() => {
    const loadSummary = async () => {
      setIsLoading(true);

      try {
        const data = await getRevenueExpensesSummary(getReferenceDate(selectedMonth));
        setSummary({
          revenue: Number(data?.revenue ?? 0),
          expenses: Number(data?.expenses ?? 0),
          remaningValue: Number(data?.remaningValue ?? 0),
        });
      } catch (error) {
        setSummary({ revenue: 0, expenses: 0, remaningValue: 0 });
        setOpenSnackMessage({ type: "error", message: `${error}` });
      } finally {
        setIsLoading(false);
      }
    };

    loadSummary();
  }, [selectedMonth]);

  useEffect(() => {
    let isMounted = true;

    const loadExpenseCategorySummary = async () => {
      setIsExpenseSummaryLoading(true);

      try {
        const data = await getRevenueExpenseCategorySummary(2, getReferenceDate(selectedMonth));

        if (!isMounted) {
          return;
        }

        setExpenseCategorySummary(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setExpenseCategorySummary([]);
        setOpenSnackMessage({ type: "error", message: `${error}` });
      } finally {
        if (isMounted) {
          setIsExpenseSummaryLoading(false);
        }
      }
    };

    loadExpenseCategorySummary();

    return () => {
      isMounted = false;
    };
  }, [selectedMonth]);

  const plannedRevenue = toNumber(budget.plannedRevenue);
  const plannedExpenses = toNumber(budget.plannedExpenses);
  const actualRevenue = toNumber(summary.revenue);
  const actualExpenses = toNumber(summary.expenses);
  const plannedBalance = plannedRevenue - plannedExpenses;
  const actualBalance = actualRevenue - actualExpenses;
  const monthProgress = useMemo(() => getMonthProgress(selectedMonth), [selectedMonth]);
  const projectedRevenue = getProjection({
    actualValue: actualRevenue,
    plannedValue: plannedRevenue,
  });
  const projectedExpenses = getProjection({
    actualValue: actualExpenses,
    plannedValue: plannedExpenses,
  });
  const projectedBalance = projectedRevenue - projectedExpenses;
  const budgetUsage = plannedExpenses > 0 ? Math.min((actualExpenses / plannedExpenses) * 100, 140) : 0;
  const visibleBudgetUsage = Math.min(budgetUsage, 100);
  const projectedExpenseDelta = projectedExpenses - plannedExpenses;
  const freeToSpend = Math.max(plannedExpenses - actualExpenses, 0);
  const expenseProgressPalette = getExpenseProgressPalette(budgetUsage);
  const budgetUsagePalette = areBudgetValuesHidden ? hiddenMetricPalette : expenseProgressPalette;
  const displayCurrency = useMemo(
    () => (value) => (areBudgetValuesHidden ? HIDDEN_CURRENCY_LABEL : formatCurrency(value)),
    [areBudgetValuesHidden]
  );
  const displayPercent = useMemo(
    () => (value) => (areBudgetValuesHidden ? HIDDEN_PERCENTAGE_LABEL : formatPercent(value)),
    [areBudgetValuesHidden]
  );
  const expenseCategoryItems = useMemo(
    () => expenseCategorySummary.map(normalizeExpenseCategorySummary).filter((item) => item.value > 0),
    [expenseCategorySummary]
  );
  const expenseCategoryTotal = useMemo(
    () => expenseCategoryItems.reduce((total, item) => total + item.value, 0),
    [expenseCategoryItems]
  );
  const expenseBreakdownTotal = expenseCategoryTotal > 0 ? expenseCategoryTotal : actualExpenses;
  const budgetInsights = useMemo(
    () =>
      buildBudgetInsights({
        categoryItems: expenseCategoryItems,
        totalExpenses: expenseBreakdownTotal,
        plannedExpenses,
        freeToSpend,
        projectedExpenseDelta,
        displayCurrency,
        displayPercent,
      }),
    [
      displayCurrency,
      displayPercent,
      expenseCategoryItems,
      expenseBreakdownTotal,
      freeToSpend,
      plannedExpenses,
      projectedExpenseDelta,
    ]
  );
  const shouldShowBudgetSummary = !isMobileBudgetSummary || isBudgetSummaryExpanded;

  const updateBudgetField = (field, value) => {
    setBudget((currentBudget) => ({
      ...currentBudget,
      [field]: value,
    }));
  };

  const saveBudgetPlan = async (plan, successMessage) => {
    try {
      await upsertBudget({
        referenceMonth: getReferenceDate(selectedMonth),
        plannedRevenue: toNumber(plan.plannedRevenue),
        plannedExpenses: toNumber(plan.plannedExpenses),
      });

      if (successMessage) {
        setOpenSnackMessage({ type: "success", message: successMessage });
      }

      return true;
    } catch (error) {
      setOpenSnackMessage({ type: "error", message: `${error}` });
      return false;
    }
  };

  const applyBudgetPlan = async (plan, successMessage) => {
    const nextPlan = {
      plannedRevenue: Number(plan?.plannedRevenue ?? 0).toFixed(2),
      plannedExpenses: Number(plan?.plannedExpenses ?? 0).toFixed(2),
    };

    skipNextPlanSaveRef.current = true;
    setBudget((currentBudget) => ({
      ...currentBudget,
      ...nextPlan,
    }));

    return saveBudgetPlan(nextPlan, successMessage);
  };

  const handleSelectedMonthChange = (monthKey) => {
    const nextMonth = monthKey || getMonthKey();

    setIsBudgetReady(false);
    setSelectedMonth(nextMonth);
    setBudget(createEmptyBudget());
  };

  const applyBudgetFromSummary = (sourceSummary, successMessage) =>
    applyBudgetPlan(
      {
        plannedRevenue: Number(sourceSummary?.revenue ?? 0).toFixed(2),
        plannedExpenses: Number(sourceSummary?.expenses ?? 0).toFixed(2),
      },
      successMessage
    );

  const handleCreateBlankBudget = () =>
    applyBudgetPlan(
      {
        plannedRevenue: "0.00",
        plannedExpenses: "0.00",
      },
      "Orçamento em branco criado."
    );

  const handleCopyPreviousMonth = async () => {
    const previousMonth = addMonths(selectedMonth, -1);

    try {
      const previousBudget = await getBudget(getReferenceDate(previousMonth));

      if (hasPersistedBudget(previousBudget)) {
        await applyBudgetPlan(
          {
            plannedRevenue: previousBudget.plannedRevenue,
            plannedExpenses: previousBudget.plannedExpenses,
          },
          "Orçamento do mês anterior copiado."
        );
        return;
      }

      const previousSummary = await getRevenueExpensesSummary(getReferenceDate(previousMonth));
      await applyBudgetFromSummary(previousSummary, "Orçamento criado com base no mês anterior.");
    } catch (error) {
      setOpenSnackMessage({ type: "error", message: `${error}` });
    }
  };

  const handleSuggestFromAverage = async () => {
    try {
      const monthKeys = [addMonths(selectedMonth, -1), addMonths(selectedMonth, -2), addMonths(selectedMonth, -3)];
      const summaries = await Promise.all(
        monthKeys.map((monthKey) => getRevenueExpensesSummary(getReferenceDate(monthKey)))
      );
      const divisor = summaries.length || 1;
      const averageRevenue = summaries.reduce((total, item) => total + Number(item?.revenue ?? 0), 0) / divisor;
      const averageExpenses = summaries.reduce((total, item) => total + Number(item?.expenses ?? 0), 0) / divisor;

      await applyBudgetPlan(
        {
          plannedRevenue: averageRevenue.toFixed(2),
          plannedExpenses: averageExpenses.toFixed(2),
        },
        "Sugestão criada pela média dos últimos 3 meses."
      );
    } catch (error) {
      setOpenSnackMessage({ type: "error", message: `${error}` });
    }
  };

  const handleResetBudget = async () => {
    try {
      await deleteBudget(getReferenceDate(selectedMonth));
    } catch (error) {
      setOpenSnackMessage({ type: "error", message: `${error}` });
      return;
    }

    try {
      skipNextPlanSaveRef.current = true;
      setBudget((currentBudget) => ({
        ...currentBudget,
        plannedRevenue: "",
        plannedExpenses: "",
      }));
    } catch (error) {
      setOpenSnackMessage({ type: "error", message: "Não foi possível limpar o orçamento." });
      return;
    }

    setOpenSnackMessage({ type: "success", message: "Orçamento resetado." });
  };

  const projectionTone =
    plannedExpenses <= 0
      ? { color: "rgb(26, 115, 232)", icon: "info", label: "Sem orçamento de despesas" }
      : projectedExpenses > plannedExpenses
      ? { color: "rgb(239, 83, 80)", icon: "warning", label: "Acima do planejado" }
      : { color: "rgb(76, 175, 80)", icon: "check_circle", label: "Dentro do planejado" };

  return (
    <DashboardLayout>
      <Grid item xs={12} sm={6} lg={3}>
        <MDSnackbar
          color={openSnackMessage?.type}
          icon="check"
          title={openSnackMessage?.message}
          open={openSnackMessage}
          onClose={() => setOpenSnackMessage(false)}
          close={() => setOpenSnackMessage(false)}
        />
      </Grid>
      <DashboardNavbar
        actions={(
          <ValueVisibilityToggleButton
            valuesHidden={areBudgetValuesHidden}
            onToggle={() => setAreBudgetValuesHidden((currentValue) => !currentValue)}
            scopeLabel="do orçamento"
          />
        )}
      />

      <MDBox mt={3}>
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} gap={2} flexDirection={{ xs: "column", md: "row" }}>
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold">
              Orçamento
            </MDTypography>
            <MDTypography variant="button" color="text">
              Planejamento mensal e projeção.
            </MDTypography>
          </MDBox>
          <TextField
            type="month"
            label="Mês"
            value={selectedMonth}
            onChange={(event) => handleSelectedMonthChange(event.target.value)}
            sx={{
              width: { xs: "100%", md: 190 },
              "& input::-webkit-calendar-picker-indicator": {
                filter: darkMode ? "invert(1)" : "none",
                opacity: 0.8,
              },
            }}
            InputLabelProps={{ shrink: true }}
          />
        </MDBox>

        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard icon="trending_up" title="Receita prevista" value={displayCurrency(plannedRevenue)} detail={`Realizado: ${displayCurrency(actualRevenue)}`} color="76, 175, 80" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard icon="trending_down" title="Despesas planejadas" value={displayCurrency(plannedExpenses)} detail={`Realizado: ${displayCurrency(actualExpenses)}`} color="239, 83, 80" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard icon="account_balance_wallet" title="Saldo previsto" value={displayCurrency(plannedBalance)} detail={`Atual: ${displayCurrency(actualBalance)}`} color="26, 115, 232" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard icon="timeline" title="Projeção do mês" value={isLoading ? "Carregando..." : displayCurrency(projectedBalance)} detail={`${projectionTone.label}`} color="251, 140, 0" />
          </Grid>
        </Grid>

        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} lg={5}>
            <Card sx={{ height: "100%" }}>
              <MDBox p={3}>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Icon color="info">edit_calendar</Icon>
                  <MDTypography variant="h6" fontWeight="medium">
                    Planejamento do mês
                  </MDTypography>
                </MDBox>

                <Grid container spacing={2} mt={1}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type={areBudgetValuesHidden ? "password" : "number"}
                      label="Receita prevista"
                      value={budget.plannedRevenue}
                      onChange={(event) => updateBudgetField("plannedRevenue", event.target.value)}
                      inputProps={{ min: 0, step: "0.01", inputMode: "decimal" }}
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type={areBudgetValuesHidden ? "password" : "number"}
                      label="Despesas planejadas"
                      value={budget.plannedExpenses}
                      onChange={(event) => updateBudgetField("plannedExpenses", event.target.value)}
                      inputProps={{ min: 0, step: "0.01", inputMode: "decimal" }}
                      autoComplete="off"
                    />
                  </Grid>
                </Grid>

                <Divider />

                <MDTypography variant="button" fontWeight="medium">
                  Ações rápidas
                </MDTypography>
                <Grid container spacing={1.5} mt={0.5}>
                  <Grid item xs={12} sm={6}>
                    <MDButton fullWidth variant="gradient" color="info" onClick={handleCreateBlankBudget}>
                      Criar do zero
                    </MDButton>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDButton fullWidth variant="outlined" color="info" onClick={handleCopyPreviousMonth}>
                      Copiar mês anterior
                    </MDButton>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDButton fullWidth variant="outlined" color="info" onClick={handleSuggestFromAverage}>
                      Média 3 meses
                    </MDButton>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDButton fullWidth variant="outlined" color="error" onClick={handleResetBudget}>
                      Resetar orçamento
                    </MDButton>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Card sx={{ height: "100%" }}>
              <MDBox p={3}>
                <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon sx={{ color: projectionTone.color }}>{projectionTone.icon}</Icon>
                    <MDTypography variant="h6" fontWeight="medium">
                      Projeção inteligente
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox mt={2}>
                  <ProjectionProgressBar
                    label="Dias decorridos no mês"
                    helperLabel={`${numberFormatter.format(monthProgress.elapsedDays)} de ${numberFormatter.format(
                      monthProgress.daysInMonth
                    )} dias`}
                    badgeLabel={`${numberFormatter.format(monthProgress.progress)}%`}
                    progress={monthProgress.progress}
                    palette={calendarProgressPalette}
                    darkMode={darkMode}
                  />
                </MDBox>

                <MDBox mt={1.5}>
                  <ProjectionProgressBar
                    label="Uso do orçamento de despesas"
                    helperLabel={plannedExpenses > 0 ? `Limite: ${displayCurrency(plannedExpenses)}` : "Sem orçamento"}
                    badgeLabel={plannedExpenses > 0 ? displayPercent(visibleBudgetUsage) : "Sem orçamento"}
                    progress={areBudgetValuesHidden ? 0 : visibleBudgetUsage}
                    palette={budgetUsagePalette}
                    darkMode={darkMode}
                  />
                </MDBox>

                <Grid container spacing={2} mt={1.5}>
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="caption" color="text">
                      Livre para gastar
                    </MDTypography>
                    <MDTypography variant="h6">{displayCurrency(freeToSpend)}</MDTypography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="caption" color="text">
                      Despesa projetada
                    </MDTypography>
                    <MDTypography variant="h6">{displayCurrency(projectedExpenses)}</MDTypography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="caption" color="text">
                      Desvio projetado de despesas
                    </MDTypography>
                    <MDTypography
                      variant="h6"
                      sx={{ color: areBudgetValuesHidden ? "inherit" : projectedExpenseDelta > 0 ? "#ef5350" : "#4caf50" }}
                    >
                      {displayCurrency(projectedExpenseDelta)}
                    </MDTypography>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        <Card>
          <MDBox
            px={3}
            pt={3}
            pb={shouldShowBudgetSummary ? 0 : 3}
            onClick={
              isMobileBudgetSummary
                ? () => setIsBudgetSummaryExpanded((currentValue) => !currentValue)
                : undefined
            }
            role={isMobileBudgetSummary ? "button" : undefined}
            aria-expanded={isMobileBudgetSummary ? isBudgetSummaryExpanded : undefined}
            sx={isMobileBudgetSummary ? { cursor: "pointer", userSelect: "none" } : undefined}
          >
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
              gap={2}
              flexDirection={{ xs: "column", md: "row" }}
            >
              <MDBox>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Icon color="info">analytics</Icon>
                  <MDTypography variant="h6" fontWeight="medium">
                    Resumo do orçamento do mês
                  </MDTypography>
                </MDBox>
                <MDTypography variant="button" color="text">
                  Despesas realizadas por categoria no mês selecionado.
                </MDTypography>
              </MDBox>
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent={{ xs: "space-between", md: "flex-end" }}
                gap={1}
                sx={{ width: { xs: "100%", md: "auto" } }}
              >
                <MDBox
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 999,
                    backgroundColor: budgetUsagePalette.soft,
                    border: `1px solid ${budgetUsagePalette.border}`,
                  }}
                >
                  <MDTypography variant="caption" fontWeight="bold" sx={{ color: budgetUsagePalette.fill }}>
                    {plannedExpenses > 0
                      ? `${displayPercent(visibleBudgetUsage)} do orçamento usado`
                      : "Sem orçamento de despesas"}
                  </MDTypography>
                </MDBox>
                {isMobileBudgetSummary && (
                  <Icon
                    sx={{
                      fontWeight: "bold",
                      color: ({ palette: { info } }) => info.main,
                      flexShrink: 0,
                    }}
                  >
                    {isBudgetSummaryExpanded ? "expand_less" : "expand_more"}
                  </Icon>
                )}
              </MDBox>
            </MDBox>
          </MDBox>

          <Collapse in={shouldShowBudgetSummary} timeout="auto" unmountOnExit>
            <MDBox px={3} pb={3} pt={2}>
              <Grid container spacing={3} mt={0}>
                <Grid item xs={12} lg={6} sx={{ order: { xs: 2, lg: 1 } }}>
                  <MDTypography variant="button" fontWeight="bold">
                    Insights do mês
                  </MDTypography>
                  <MDBox mt={2}>
                    {isExpenseSummaryLoading ? (
                      <MDTypography variant="button" color="text">
                        Carregando insights...
                      </MDTypography>
                    ) : (
                      <BudgetInsightList insights={budgetInsights} darkMode={darkMode} />
                    )}
                  </MDBox>
                </Grid>

                <Grid item xs={12} lg={6} sx={{ order: { xs: 1, lg: 2 } }}>
                  <MDTypography variant="button" fontWeight="bold">
                    Distribuição das despesas
                  </MDTypography>
                  <MDBox mt={2}>
                    {isExpenseSummaryLoading ? (
                      <MDTypography variant="button" color="text">
                        Carregando despesas...
                      </MDTypography>
                    ) : (
                      <ExpenseDistributionChart
                        items={expenseCategoryItems}
                        total={expenseBreakdownTotal}
                        darkMode={darkMode}
                        displayCurrency={displayCurrency}
                        displayPercent={displayPercent}
                        valuesHidden={areBudgetValuesHidden}
                      />
                    )}
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </Collapse>
        </Card>

      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default Orcamento;
